/**
 * Shared PDP product-swap helpers used by size and option pickers.
 * Swaps product-info in place and syncs template-specific page content.
 */
window.PdpProductSwap = {
  getProductInfo(host) {
    return host.closest('product-info');
  },

  updateCurrentBreadcrumb(title) {
    const crumb = document.querySelector('.breadcrumbs__item--current');
    if (!crumb || !title) return;
    crumb.textContent = title;
  },

  updateProductForm(productInfo, variantId, available) {
    if (!variantId) return;

    const sectionId = productInfo?.dataset?.section;
    const forms = sectionId
      ? productInfo.querySelectorAll(
          `#product-form-${sectionId}, #product-form-installment-${sectionId}`
        )
      : document.querySelectorAll('form[data-type="add-to-cart-form"]');

    forms.forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (!input) return;

      input.value = variantId;
      input.disabled = !available;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      const productForm = form.closest('product-form');
      if (!productForm?.toggleSubmitButton) return;

      if (available) {
        productForm.toggleSubmitButton(false);
      } else {
        productForm.toggleSubmitButton(true, window.variantStrings?.soldOut);
      }
    });
  },

  replaceNodeContent(destination, source) {
    if (!destination || !source) return;
    destination.innerHTML = source.innerHTML;
  },

  getMainContentSections(root) {
    const main = root.querySelector('#MainContent') || root.querySelector('main[role="main"]');
    if (!main) return [];
    return [...main.children].filter((el) => el.id?.startsWith('shopify-section-'));
  },

  syncDropdownsFromPage(productInfoNode, pageHtml, openState = {}) {
    pageHtml.querySelectorAll('[data-dropdown-block]').forEach((source) => {
      const blockId = source.dataset.dropdownBlock;
      if (!blockId) return;

      const destination = productInfoNode.querySelector(
        `[data-dropdown-block="${CSS.escape(blockId)}"]`
      );
      if (!destination) return;

      const replacement = source.cloneNode(true);
      if (Object.prototype.hasOwnProperty.call(openState, blockId)) {
        if (openState[blockId]) {
          replacement.setAttribute('open', '');
          replacement.classList.add('is-open');
        } else {
          replacement.removeAttribute('open');
          replacement.classList.remove('is-open');
        }
      }

      destination.replaceWith(replacement);
    });
  },

  syncProductInfoBlocks(productInfoNode, pageHtml) {
    const pageProductInfo = pageHtml.querySelector('product-info');
    if (!pageProductInfo) return;

    this.replaceNodeContent(
      productInfoNode.querySelector('.product__benefits'),
      pageProductInfo.querySelector('.product__benefits')
    );

    this.replaceNodeContent(
      productInfoNode.querySelector('.pdp-reviews-block__body'),
      pageProductInfo.querySelector('.pdp-reviews-block__body')
    );

    productInfoNode.querySelectorAll('.pdp-gallery-icons').forEach((destination) => {
      const placementClass = [...destination.classList].find((className) =>
        className.startsWith('pdp-gallery-icons--')
      );
      const source = placementClass
        ? pageProductInfo.querySelector(`.${placementClass}`)
        : pageProductInfo.querySelector('.pdp-gallery-icons');
      if (source) destination.replaceWith(source.cloneNode(true));
    });

    this.syncFilterInfoModals(pageHtml);
  },

  removeFilterInfoModals() {
    document.querySelectorAll('modal-dialog[id^="FilterInfoModal-"]').forEach((modal) => {
      if (modal.hasAttribute('open') && typeof modal.hide === 'function') modal.hide();
      modal.remove();
    });
  },

  syncFilterInfoModals(pageHtml) {
    const sources = [...pageHtml.querySelectorAll('modal-dialog[id^="FilterInfoModal-"]')];
    if (!sources.length) return;

    const destinations = [...document.querySelectorAll('modal-dialog[id^="FilterInfoModal-"]')];
    const suffix = (id) => id.match(/(\d+)$/)?.[1] || '';

    sources.forEach((source, index) => {
      const key = suffix(source.id);
      const destination =
        (key && destinations.find((modal) => suffix(modal.id) === key)) || destinations[index];
      if (!destination) return;

      const sourceLabel = source.querySelector('[role="dialog"]')?.getAttribute('aria-label');
      const destDialog = destination.querySelector('[role="dialog"]');
      if (sourceLabel && destDialog) destDialog.setAttribute('aria-label', sourceLabel);

      const sourceHeader = source.querySelector('.filter-info-modal__header');
      const destHeader = destination.querySelector('.filter-info-modal__header');
      const destClose = destHeader?.querySelector('.filter-info-modal__close');
      if (sourceHeader && destHeader && destClose) {
        const sourceLead =
          sourceHeader.querySelector('.filter-info-modal__eyebrow') || sourceHeader.firstElementChild;
        [...destHeader.children].forEach((child) => {
          if (child !== destClose) child.remove();
        });
        if (sourceLead && !sourceLead.classList.contains('filter-info-modal__close')) {
          destClose.insertAdjacentElement('beforebegin', sourceLead.cloneNode(true));
        }
      }

      const sourceBody = source.querySelector('.filter-info-modal__body');
      const destBody = destination.querySelector('.filter-info-modal__body');
      if (sourceBody && destBody) destBody.innerHTML = sourceBody.innerHTML;
    });
  },

  syncMainContentSections(pageHtml) {
    const liveSections = this.getMainContentSections(document);
    const pageSections = this.getMainContentSections(pageHtml);
    const count = Math.min(liveSections.length, pageSections.length);

    for (let index = 0; index < count; index++) {
      const destination = liveSections[index];
      const source = pageSections[index];
      if (!destination || !source) continue;
      if (destination.querySelector('product-info') || source.querySelector('product-info')) continue;
      if (destination.textContent === source.textContent) continue;
      if (typeof HTMLUpdateUtility?.setInnerHTML === 'function') {
        HTMLUpdateUtility.setInnerHTML(destination, source.innerHTML);
      } else {
        destination.innerHTML = source.innerHTML;
      }
    }
  },

  syncPageChrome(pageHtml) {
    const pageTitle = pageHtml.querySelector('title')?.textContent?.trim();
    if (pageTitle) document.title = pageTitle;

    this.syncMainContentSections(pageHtml);
  },

  syncStickyFromSection(sectionHtml, sectionId) {
    if (!sectionId) return;

    const source = sectionHtml.getElementById(`StickyAtcContent-${sectionId}`);
    const sticky = document.querySelector('sticky-atc');
    const destination =
      sticky?.querySelector(`#StickyAtcContent-${sectionId}`) ||
      document.getElementById(`StickyAtcContent-${sectionId}`);

    if (!source || !destination) return;

    destination.innerHTML = source.innerHTML;
    if (sticky) sticky.content = destination;
    sticky?.syncButtonState?.();
  },

  /**
   * @param {object} options
   * @param {HTMLElement} options.host - Element owning dataset.loading
   * @param {string} options.productUrl
   * @param {string} options.productId
   * @param {string} [options.variantId]
   * @param {boolean} [options.available]
   * @param {string} [options.productTitle]
   * @param {string} [options.focusSelector] - Query selector to focus after swap
   */
  swapProduct({ host, productUrl, productId, variantId, available, productTitle, focusSelector }) {
    const productInfo = this.getProductInfo(host);
    if (!productInfo || !productUrl) return;

    this.updateProductForm(productInfo, variantId, available);
    this.updateCurrentBreadcrumb(productTitle);

    if (productInfo.dataset.url === productUrl) {
      productInfo.updateURL?.(productUrl, variantId);
      return;
    }

    host.dataset.loading = 'true';
    productInfo.dataset.productId = productId;

    const sectionId = productInfo.dataset.section;
    const requestUrl = sectionId
      ? `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=${sectionId}`
      : productUrl;

    const openDropdownState = {};
    productInfo.querySelectorAll('[data-dropdown-block]').forEach((el) => {
      openDropdownState[el.dataset.dropdownBlock] = el.hasAttribute('open');
    });

    // Full product page uses the destination template's block settings (dropdown copy).
    // Section_id fetch keeps stable section ids but reuses the current template's static content.
    const dropdownPagePromise = fetch(productUrl)
      .then((response) => response.text())
      .then((text) => new DOMParser().parseFromString(text, 'text/html'))
      .catch(() => null);

    // Do not use handleSwapProduct/viewTransition: it inserts the new node above the old one,
    // doubling section height and triggering browser scroll-anchoring (jump down).
    // In-place replaceWith keeps layout height stable so scroll position stays put.
    productInfo.renderProductInfo({
      requestUrl,
      targetId: null,
      callback: (html) => {
        const newContent = html.querySelector('product-info');
        if (!newContent) {
          host.dataset.loading = 'false';
          return;
        }

        const preProcess = [...(productInfo.preProcessHtmlCallbacks || [])];
        const postProcess = [...(productInfo.postProcessHtmlCallbacks || [])];

        productInfo.productModal?.remove();
        const variant = productInfo.getSelectedVariant(newContent);
        productInfo.updateURL(productUrl, variant?.id || variantId);

        preProcess.forEach((cb) => cb(newContent));

        const wrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(wrapper, newContent.outerHTML);
        const newNode = wrapper.firstElementChild;

        this.removeFilterInfoModals();
        productInfo.replaceWith(newNode);
        postProcess.forEach((cb) => cb(newNode));
        this.updateCurrentBreadcrumb(
          newContent.querySelector('.product__title h1')?.textContent?.trim() || productTitle
        );

        this.syncStickyFromSection(html, sectionId);

        dropdownPagePromise.then((pageHtml) => {
          if (!pageHtml || !newNode.isConnected) return;
          this.syncDropdownsFromPage(newNode, pageHtml, openDropdownState);
          this.syncProductInfoBlocks(newNode, pageHtml);
          this.syncPageChrome(pageHtml);
        });

        if (focusSelector) {
          requestAnimationFrame(() => {
            document.querySelector(focusSelector)?.focus({ preventScroll: true });
          });
        }
      },
    });
  },
};
